import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import { Box, Paper, Typography } from "@mui/material";
import ReactECharts from "echarts-for-react";
import { useCallback, useMemo, useState } from "react";

import {
	AXIS_HELP,
	buildFixedToRealTime,
	formatDuration,
	RFACTOR_AXIS_MAX,
	RFACTOR_AXIS_MIN,
	RFACTOR_AXIS_STEP,
	type RFactorPerStandData,
	SERIES_COLORS,
} from "../models/RFactorPerStand";

interface AxisEventParams {
	componentType?: string;
	targetType?: string;
	event?: { event?: { clientX: number; clientY: number } };
}

interface HelpPanel {
	text: string;
	x: number;
	y: number;
}

interface TooltipParam {
	seriesName: string;
	value: [number, number | null];
}

const clock = (ms: number, format: string) =>
	dxtToLocalServerTime(new Date(ms), format);

interface Props {
	data: RFactorPerStandData;
	emptyReason: string;
}

export const RFactorPerStandChart = ({ data, emptyReason }: Props) => {
	const [help, setHelp] = useState<HelpPanel | null>(null);

	const option = useMemo(() => {
		const { points, hollows, shiftChanges } = data;
		const fixedToReal = buildFixedToRealTime(points);

		// The inverse of the same step function, for annotations that arrive in
		// real time and have to be placed on the compressed axis.
		const realToFixed = (real: number) => {
			let offset = points.length ? points[0].start - points[0].startFixed : 0;
			for (const point of points) {
				if (point.start > real) break;
				offset = point.start - point.startFixed;
			}
			return real - offset;
		};

		// Min and Max sit on top of the average for most billets, so drawing them
		// at full weight hides the series the chart is about. They stay behind it
		// and thin: what they carry is the spread, not a third reading.
		const series = (
			[
				["R-Factor", "avg", SERIES_COLORS.avg, 2, 1, 3],
				["Min", "min", SERIES_COLORS.limits, 1, 0.5, 1],
				["Max", "max", SERIES_COLORS.limits, 1, 0.5, 1],
			] as const
		).map(([name, key, color, width, opacity, z]) => ({
			name,
			type: "line" as const,
			// A line through one point draws nothing, and through two draws a stub
			// that is easy to miss. A short series gets its markers back.
			showSymbol: points.length <= 5,
			symbolSize: 5,
			connectNulls: false,
			z,
			lineStyle: { color, width, opacity },
			itemStyle: { color },
			data: points.map((p) => [p.startFixed, p[key]]),
		}));

		// A bare crew letter next to a bare number tells an operator nothing about
		// which line is which. Both marks say what they are, and both carry the
		// whole sentence plus the real clock time on hover.
		const markLineData = [
			...shiftChanges.map((change) => ({
				name: `Shift change at ${clock(
					fixedToReal(change.timestampFixed),
					"MM/dd/yyyy HH:mm",
				)}. Crew ${change.crew} took over.`,
				xAxis: change.timestampFixed,
				lineStyle: {
					color: SERIES_COLORS.shiftChange,
					width: 2,
					type: "dashed" as const,
				},
				label: {
					formatter: `Crew ${change.crew}`,
					position: "end" as const,
					rotate: 0,
					distance: 4,
					color: SERIES_COLORS.shiftChange,
					fontWeight: "bold" as const,
				},
			})),
			...hollows.map((hollow) => ({
				name: `The mill did not roll this product between ${clock(
					hollow.start,
					"MM/dd/yyyy HH:mm",
				)} and ${clock(hollow.end, "MM/dd/yyyy HH:mm")}, ${formatDuration(
					hollow.seconds,
				)}. That time is cut out of the axis.`,
				xAxis: realToFixed(hollow.start),
				// Solid, explicitly: echarts draws every mark line dashed unless told
				// otherwise, which left the two kinds of mark telling apart by colour
				// alone.
				lineStyle: {
					color: SERIES_COLORS.hollow,
					width: 2,
					type: "solid" as const,
				},
				// Offset to its own row, to the right of the line: a collapsed period
				// often falls next to a shift boundary, and centred on the line the
				// text would sit on both the line and the crew letter.
				label: {
					formatter: `${formatDuration(hollow.seconds)} not rolled`,
					position: "end" as const,
					rotate: 0,
					distance: 4,
					align: "left" as const,
					offset: [6, 18],
					color: SERIES_COLORS.hollow,
				},
			})),
		];

		if (markLineData.length) {
			(series[0] as Record<string, unknown>).markLine = {
				symbol: "none",
				data: markLineData,
				emphasis: { lineStyle: { width: 4 } },
				tooltip: {
					trigger: "item",
					formatter: (params: { name?: string }) => params.name ?? "",
				},
			};
		}

		const xs = points.map((p) => p.startFixed);
		const xMin = xs.length ? Math.min(...xs) : 0;
		const xMax = xs.length ? Math.max(...xs) : 0;
		const flat = xMax - xMin < 60_000;
		const pad = flat ? 30 * 60_000 : 0;

		return {
			animation: false,
			tooltip: {
				trigger: "axis",
				axisPointer: { type: "line" },
				formatter: (params: TooltipParam[]) => {
					if (!params.length) return "";
					const real = fixedToReal(params[0].value[0]);
					const lines = params
						.filter((p) => p.value[1] != null)
						.map(
							(p) =>
								`${p.seriesName}: <b>${(p.value[1] as number).toFixed(3)}</b>`,
						);
					return [clock(real, "MM/dd/yyyy HH:mm:ss"), ...lines].join("<br/>");
				},
			},
			legend: { top: 4, textStyle: { fontSize: 11 } },
			// The mark labels sit at the top of the plot, so the grid starts below the
			// legend rather than level with it.
			grid: { left: 56, right: 44, top: 56, bottom: 44 },
			xAxis: {
				type: "value",
				name: "Time",
				nameLocation: "middle",
				nameGap: 28,
				triggerEvent: true,
				min: xMin - pad,
				max: xMax + pad,
				axisLabel: {
					fontSize: 11,
					hideOverlap: true,
					formatter: (value: number) =>
						clock(fixedToReal(value), "MM/dd HH:mm"),
				},
				splitLine: { show: false },
			},
			yAxis: {
				type: "value",
				name: "R-Factor",
				nameLocation: "middle",
				nameGap: 40,
				triggerEvent: true,
				min: RFACTOR_AXIS_MIN,
				max: RFACTOR_AXIS_MAX,
				interval: RFACTOR_AXIS_STEP,
				axisLabel: {
					fontSize: 11,
					formatter: (value: number) => value.toFixed(2),
				},
			},
			series,
		};
	}, [data]);

	const handleAxisOver = useCallback((params: AxisEventParams) => {
		if (params?.targetType !== "axisName") return;
		const native = params.event?.event;
		setHelp({
			text: params.componentType === "xAxis" ? AXIS_HELP.x : AXIS_HELP.y,
			x: native?.clientX ?? 0,
			y: native?.clientY ?? 0,
		});
	}, []);

	const handleAxisOut = useCallback((params: AxisEventParams) => {
		if (params?.targetType !== "axisName") return;
		setHelp(null);
	}, []);

	const onEvents = useMemo(
		() => ({ mouseover: handleAxisOver, mouseout: handleAxisOut }),
		[handleAxisOver, handleAxisOut],
	);

	// An empty series has no axis worth drawing: pinned to epoch zero, echarts
	// labels it 12/31/1969 and draws a stray line. The reason goes where the
	// chart would have been, which is where the legacy puts it too.
	if (!data.points.length) {
		return (
			<Box
				sx={{
					height: "100%",
					width: "100%",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
				}}
			>
				<Typography variant="h6" color="text.disabled">
					{emptyReason}
				</Typography>
			</Box>
		);
	}

	return (
		<Box sx={{ height: "100%", width: "100%" }}>
			<ReactECharts
				option={option}
				notMerge
				lazyUpdate
				onEvents={onEvents}
				style={{ height: "100%", width: "100%" }}
			/>
			{help && (
				<Paper
					elevation={4}
					sx={{
						position: "fixed",
						left: Math.min(help.x + 12, window.innerWidth - 340),
						top: Math.max(help.y - 84, 8),
						zIndex: (theme) => theme.zIndex.tooltip,
						maxWidth: 320,
						px: 1.25,
						py: 0.75,
						pointerEvents: "none",
						bgcolor: "rgba(38,38,38,0.94)",
					}}
				>
					<Typography variant="caption" sx={{ color: "#fff", lineHeight: 1.4 }}>
						{help.text}
					</Typography>
				</Paper>
			)}
		</Box>
	);
};
